import { useContext, useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import AdminRow from "../components/display/AdminRow.jsx";
import BreadCrumb from "../components/breadCrumb/BreadCrumb.jsx";
import RbA from "../components/rba/RbA.jsx";
import SchoolAdmin from "./SchoolAdmin.jsx";
import TeacherHome from "./TeacherHome.jsx";
import RegionOverviewHome from "../components/teacherOverview/RegionOverviewHome.jsx";

import { EVALUATION_SITE } from "../utils/auth/config.js";
import { GlobalContext } from "../components/contextProvider/ContextProvider";
import { hasRole, isUserDistrictAdmin, isUserSchoolAdmin } from "../utils/isUser.jsx";
import { SCHOOL_KEY_EXCEPTION } from "../const/LocationConst.js";

import "../styles/global/Global.scss";
import "../styles/Home.scss";

/**
 * This is the home page for epr
 * @name Home
 * @returns {JSX.Element}
 */
const Home = () => {
    const { state, dispatch } = useContext(GlobalContext);
    const { userDetails, isDistrictViewingSchool } = state || {};

    const [isDistrict, setIsDistrict] = useState(false);
    const [isSchoolAdmin, setIsSchoolAdmin] = useState(false);
    const [isElementary, setIsElementary] = useState(false);
    const [isHighSchool, setIsHighSchool] = useState(false);
    const [isMiddle, setIsMiddle] = useState(false);
    const [isPK12, setIsPK12] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { pathname } = location;

    const allowedRolesArray = ["EPR_ADMIN", "PK12", "PRINCIPAL", "ASSISTANT_PRINCIPAL", "REGISTRAR", "SDIS", "TEACHER"];

    /**
     * @name handleRouting
     * @returns
     */
    const handleRouting = (e) => {
        const { name } = e.target;
        if (name === "school-view") {
            dispatch({
                type: "IsDistrictViewingSchool",
                isDistrictViewingSchool: true
            });
            return;
        }
        navigate(`/${name}`);
    };

    /* Determine if user is district level or school and if school if they are admin */
    useEffect(() => {
        const locationType = userDetails?.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL?.locationType;
        const eDcsdEmployee =
            userDetails?.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL?.locationKey ===
            SCHOOL_KEY_EXCEPTION;

        if (isUserDistrictAdmin(userDetails)) {
            setIsDistrict(true);
        }

        if (isUserSchoolAdmin(userDetails)) {
            setIsSchoolAdmin(true);
        }

        if (hasRole(userDetails, ["PK12"])) {
            setIsPK12(true);
        }

        if (eDcsdEmployee) {
            setIsElementary(true);
        }

        switch (locationType) {
            case "ELEMENTARY_SCHOOL":
                setIsElementary(true);
                break;
            case "MIDDLE_SCHOOL":
                setIsMiddle(true);
                break;
            case "HIGH_SCHOOL":
            case "ALTERNATIVE_SCHOOL":
                setIsHighSchool(true);
                break;
            default:
                break;
        }
    }, [userDetails]);

    /**
     * Parse newly opened tabs from Region Overview to initialize context
     */
    useEffect(() => {
        const initKey = sessionStorage.getItem("initViewedSchoolKey");
        const existingKey = sessionStorage.getItem("viewedSchoolKey");

        if (!(isDistrict || isPK12) || (!initKey && !existingKey)) return;

        if (initKey) {
            sessionStorage.setItem("viewedSchoolKey", initKey);
            sessionStorage.removeItem("initViewedSchoolKey");
        }

        dispatch({
            type: "IsDistrictViewingSchool",
            isDistrictViewingSchool: true
        });
    }, [isDistrict, isPK12, dispatch]);

    /**
     * CHARTER_EMPLOYEE role has no access
     */
    useEffect(() => {
        if (userDetails) {
            if (
                hasRole(userDetails, ["CHARTER_EMPLOYEE"]) ||
                (hasRole(userDetails, ["TEACHER"]) && isHighSchool && !isElementary)
            ) {
                navigate("/notFound");
            } else if (hasRole(userDetails, ["GUARDIAN"])) {
                navigate("/guardian");
            }
        }
    }, [isElementary, isHighSchool, navigate, userDetails]);

    const isTeacher = hasRole(userDetails, ["TEACHER"]);
    const isRegistrarOrSdis = hasRole(userDetails, ["REGISTRAR", "SDIS"]);

    const isOverview = pathname.includes("overview") || pathname.includes("template") || pathname.includes("sles");

    const isReportCardOverview = pathname.includes("report_card_overview");
    const canViewRegionOverview = Boolean(isUserDistrictAdmin(userDetails) || hasRole(userDetails, ["PK12"]));
    const isRegionOverview = isReportCardOverview && canViewRegionOverview;
    const isUnauthorizedRegionOverview = isReportCardOverview && userDetails && !canViewRegionOverview;

    const showDistrict = isDistrict && !isDistrictViewingSchool && !isOverview;
    const showPK12Dashboard = isPK12 && !isDistrict && !isOverview && !isDistrictViewingSchool;
    const showSchoolAdmin =
        ((!isDistrict && isSchoolAdmin && isElementary) ||
            isDistrictViewingSchool ||
            (isDistrict && isOverview) ||
            (isPK12 && isOverview)) &&
        !isRegionOverview;
    const showTeacherHome = !isDistrict && !isSchoolAdmin && isTeacher && isElementary;
    const predominantSchool = userDetails?.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL;

    const showStudentSearchOnly =
        !isDistrict &&
        ((isSchoolAdmin && !isElementary && predominantSchool) ||
            (isTeacher && isMiddle) ||
            (!isSchoolAdmin && isRegistrarOrSdis));

    if (isUnauthorizedRegionOverview) {
        return <Navigate to="/notFound" replace />;
    }

    return (
        <RbA allowedRoles={allowedRolesArray} redirect="/notFound">
            <ToastContainer style={{ width: "50%" }} />

            {showDistrict && (
                <>
                    <BreadCrumb labelObj={[{ label: "Home", path: "/home" }]} />
                    <div className="gutter-90">
                        <hr />
                        <AdminRow
                            handleRouting={handleRouting}
                            header="Select Reportable Content"
                            icon="HAMBURGER"
                            message1="Select all reportable content items from the Colorado Academic Standards as the basis for the default district Report Card template"
                            name="manage-epr-content"
                        />
                        <hr />
                        <AdminRow
                            handleRouting={handleRouting}
                            header="Manage District Template"
                            icon="HAMBURGER"
                            message1="Create, validate and release the default district Report Card template for the current school year"
                            name="district-template"
                        />
                        <hr />
                        <AdminRow
                            handleRouting={handleRouting}
                            header="Student Search"
                            icon="HAMBURGER"
                            message1="Search for students to view/download Archived Report Cards"
                            name="student-search"
                        />
                        <hr />
                        <AdminRow
                            handleRouting={handleRouting}
                            header="Report Card Overview"
                            icon="HAMBURGER"
                            message1="View Report Card metrics and teacher progress across the district"
                            name="home/report_card_overview"
                        />

                        {/* Saving for future */}
                        {/* <hr />
                    <AdminRow
                        handleRouting={handleRouting}
                        header="School Admin View"
                        icon="HAMBURGER"
                        message1="View a school as a school admin would."
                        name="school-view"
                    /> */}
                    </div>
                </>
            )}

            {showPK12Dashboard && (
                <>
                    <BreadCrumb labelObj={[{ label: "Home", path: "/home" }]} />
                    <div className="gutter-90">
                        <hr />
                        <AdminRow
                            handleRouting={handleRouting}
                            header="Student Search"
                            icon="HAMBURGER"
                            message1="Search for students to view/download Archived Report Cards"
                            name="student-search"
                        />
                        <hr />
                        <AdminRow
                            handleRouting={handleRouting}
                            header="Report Card Overview"
                            icon="HAMBURGER"
                            message1="View Report Card metrics and teacher progress across the district"
                            name="home/report_card_overview"
                        />
                    </div>
                </>
            )}

            {showSchoolAdmin && <SchoolAdmin isDistrict={isDistrict} isPK12={isPK12} />}
            {isRegionOverview && <RegionOverviewHome />}
            {(isTeacher || isSchoolAdmin) && !predominantSchool && (
                <div
                    style={{
                        border: "1px solid black",
                        borderRadius: "5px",
                        padding: "12px 24px",
                        margin: "24px 0px",
                        width: "fit-content"
                    }}
                >
                    <h5>No Location Found</h5>
                    <div>A primary location is required before you can access Report Card.</div>
                    <div>
                        Please update your employee profile{" "}
                        <a href={`${EVALUATION_SITE}/profile`} target="_blank" rel="noreferrer">
                            here.
                        </a>
                    </div>
                </div>
            )}
            {showTeacherHome && <TeacherHome />}

            {showStudentSearchOnly && (
                <>
                    <BreadCrumb labelObj={[{ label: "Home", path: "/home" }]} />
                    <div className="gutter-90">
                        <hr />
                        <AdminRow
                            handleRouting={handleRouting}
                            header="Student Search"
                            icon="HAMBURGER"
                            message1="Search for students to view/download Archived Report Cards"
                            name="student-search"
                        />
                    </div>
                </>
            )}
        </RbA>
    );
};

export default Home;
